import React, { useRef, useEffect } from 'react';
import MessageFormatter from '../MessageFormatter';
import { t } from '../../utils/translations';

const ChatTab = ({
  currentConversation,
  messages,
  inputMessage,
  loading,
  loadingUserInfo,
  loadingConversations,
  userName,
  userLanguage,
  onSendMessage,
  setInputMessage
}) => {
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleKeyDown = (e) => {
    // Shift+Enter creates new line, Enter sends message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading && inputMessage.trim()) {
        onSendMessage(e);
      }
    }
    // Allow Shift+Enter to create new line (default behavior)
  };

  const handleInput = (e) => {
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  return (
    <div className="chat-tab">
      <div className="chat-messages" style={{
        background: 'white',
        borderRadius: '8px',
        padding: '1.5rem',
        minHeight: '400px',
        maxHeight: '60vh',
        overflowY: 'auto',
        marginBottom: '1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {loadingUserInfo ? (
          <div className="welcome-screen">
            <div className="loading">{t('loading', userLanguage)}</div>
          </div>
        ) : messages.length === 0 && !loading && !loadingConversations && !currentConversation ? (
          <div className="welcome-screen">
            <h2>{t('welcome', userLanguage)}</h2>
            <p>
              {t('subjectsDescription', userLanguage)}
            </p>
            <p style={{ marginTop: '1rem', color: '#718096' }}>
              {t('selectConversationHint', userLanguage)}
            </p>
          </div>
        ) : messages.length > 0 ? (
          messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div className="message-content">
                <div className="message-role">
                  {message.role === 'user' ? (userName || t('you', userLanguage)) : t('assistant', userLanguage)} :
                </div>
                {message.role === 'assistant' ? (
                  <MessageFormatter content={message.content} userLanguage={userLanguage} />
                ) : (
                  <div>{message.content}</div>
                )}
              </div>
            </div>
          ))
        ) : loadingConversations ? (
          <div className="welcome-screen">
            <div className="loading">{t('loading', userLanguage)}</div>
          </div>
        ) : null}

        {loading && (
          <div className="message assistant">
            <div className="message-content">
              <div className="loading">
                <div className="spinner"></div>
                {t('assistantTyping', userLanguage)}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input" style={{
        background: 'white',
        borderRadius: '8px',
        padding: '1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <form onSubmit={onSendMessage} className="input-group chat-input-form">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={t('typeMessage', userLanguage)}
            disabled={loading}
            rows={1}
            className="chat-textarea"
            onKeyDown={handleKeyDown}
            onInput={handleInput}
          />
          <button 
            type="submit" 
            disabled={loading || !inputMessage.trim()}
            className="btn chat-send-btn"
          >
            {t('send', userLanguage)}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatTab;

